import * as React from 'react';

import { Box, IconButton, MenuItem, Select } from '@mui/material';

import { Close } from '@mui/icons-material';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import { observer } from 'mobx-react';

import { UuidableName } from 'utils/api/types';
import { ProtectorType } from 'core/constants';

import { OPERATORS } from 'utils/api/modules/entities/entities.types';
import EntitiesDropDownField from 'containers/EntitiesFormSubmission/components/EntitiesDropDownField';

import EntitySearchField from 'components/EntitySearchField';

import { useStore } from 'utils/hooks/store';

import { AssignmentRuleType } from 'stores/AssignmentRules/AssignmentRules';

import FormsSearchField from './FormsSearchField';
import { ConditionContainer, ConditionText, FieldLabel } from './styles';

interface AssignmentConditionProps {
  condition: AssignmentRuleType['conditions'][number];
  index: number;
  rule: Pick<AssignmentRuleType, 'uuid' | 'conditions'>;
}

const AssignmentCondition = ({
  condition,
  index,
  rule: { uuid: ruleUuid, conditions },
}: AssignmentConditionProps): React.ReactElement => {
  const { assignmentRules } = useStore();
  const operator = Object.keys(condition)[0];
  const [field, values] = condition[operator];

  const fieldData = assignmentRules.fields.find(({ key }) => key === field);

  const activeOptions = fieldData?.properties?.enum?.filter(({ active }) => active);

  const onChange = (value: AssignmentRuleType['conditions'][number]): void => {
    assignmentRules.updateConditionGeneric(ruleUuid, index, value);
  };

  const valueError =
    assignmentRules.ruleErrors[ruleUuid]?.fieldKey === 'conditions' &&
    assignmentRules.ruleErrors[ruleUuid]?.conditionIndex === index &&
    assignmentRules.ruleErrors[ruleUuid]?.error;

  return (
    <ConditionContainer>
      <ConditionText>{index === 0 ? 'IF' : 'AND'}</ConditionText>
      <Box sx={{ flex: 2 }}>
        <FieldLabel>Field</FieldLabel>
        <EntitiesDropDownField
          value={[field]}
          description="Field description"
          type={ProtectorType.SingleChoice}
          onChange={(value) => {
            const newField = value[0];
            // Clear values when field is cleared or changed to a different field
            const newValues = newField === '' || newField !== field ? [] : [];
            onChange({ [operator]: [newField, newValues] });
          }}
          items={assignmentRules.fields.map(({ key, name }) => ({ key, value: name }))}
        />
      </Box>
      <Box sx={{ flex: 1 }}>
        <FieldLabel>Operator</FieldLabel>
        <Select
          fullWidth
          sx={{
            backgroundColor: 'white',
            minHeight: '56px',
            '.MuiSelect-select': {
              fontSize: 'inherit',
            },
          }}
          value={operator}
          IconComponent={KeyboardArrowDownIcon}
          onChange={(e) => onChange({ [e.target.value]: [field, values] })}
          renderValue={(value) => (
            <Box component="span" sx={{ fontSize: '16px' }}>
              {value.replaceAll('_', ' ')}
            </Box>
          )}
        >
          <MenuItem value={OPERATORS.is_any_of || OPERATORS.submission_form}>is any of</MenuItem>
          {fieldData?.key !== OPERATORS.submission_form && <MenuItem value={OPERATORS.is_none_of}>is none of</MenuItem>}
        </Select>
      </Box>
      <Box sx={{ flex: 2 }}>
        <FieldLabel>Value</FieldLabel>
        {fieldData?.protectorType === ProtectorType.Lookup ? (
          <EntitySearchField
            field="lookup"
            updateField={(f: string, val: UuidableName | UuidableName[] | null) => {
              if (Array.isArray(val)) {
                onChange({ [operator]: [field, val] });
              }
            }}
            data={Array.isArray(values) ? values.map((v) => ('uuid' in v ? v : { uuid: v.key, name: v.value })) : []}
            module={fieldData.properties?.lookupType || ''}
            multiple
            error={assignmentRules.ruleErrors[ruleUuid]?.fieldKey === 'conditions'}
          />
        ) : fieldData?.key === OPERATORS.submission_form ? (
          <FormsSearchField
            value={Array.isArray(values) ? values.map((v) => ('key' in v ? v : { key: v.uuid, value: v.name })) : []}
            onChange={(items) => onChange({ [operator]: [field, items] })}
            error={!!valueError}
            helperText={valueError || undefined}
          />
        ) : (
          <EntitiesDropDownField
            value={Array.isArray(values) ? values.map((v) => ('key' in v ? v.key : v.uuid)) : []}
            description="Field description"
            type={ProtectorType.MultiChoice}
            items={activeOptions || []}
            onChange={(value) => {
              // value is an array of selected keys/uuids
              const selectedItems = value.map((key) => {
                const option = activeOptions?.find((opt) => opt.key === key);
                return option ? { key: option.key, value: option.value } : { key, value: key };
              });
              onChange({ [operator]: [field, selectedItems] });
            }}
            error={!!valueError}
            helperText={valueError || undefined}
          />
        )}
      </Box>

      {conditions.length > 1 && (
        <IconButton onClick={() => assignmentRules.removeConditionGeneric(ruleUuid, index)}>
          <Close />
        </IconButton>
      )}
    </ConditionContainer>
  );
};

export default observer(AssignmentCondition);
